"use client";

import React, { useEffect, useState } from "react";
import { PuffLoader } from "react-spinners";
import useProperties from "@/components/hooks/useProperties";
import DisplayGrid, { createGridFilter } from "@/components/DisplayGrid/DisplayGrid";
import { getPropertyRows } from "@/utils/api";
import { getLogger } from "@/utils/logger";

const log = getLogger("JVFeaturedProperties");

// Admin-curated row (see PropertyRowManager) that lists which properties
// are part of the joint venture program. Falls back to an empty-state
// message until a "JV" row has been created for this site.
const JV_ROW_TYPE = "JV";

const JVFeaturedProperties = () => {
  const { data, isLoading, isError } = useProperties();
  const [jvPropertyIds, setJvPropertyIds] = useState([]);
  const [loadingRow, setLoadingRow] = useState(true);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const fetchJVRow = async () => {
      setLoadingRow(true);
      try {
        const rows = await getPropertyRows(JV_ROW_TYPE);
        const jvRow = Array.isArray(rows)
          ? rows.find((row) => row.rowType === JV_ROW_TYPE)
          : null;

        if (jvRow && Array.isArray(jvRow.displayOrder)) {
          const propertiesMap = new Map(data.map((property) => [property.id, property]));
          const ids = jvRow.displayOrder.filter((id) => propertiesMap.has(id));
          setJvPropertyIds(ids);
        } else {
          setJvPropertyIds([]);
        }
      } catch (error) {
        log.error(
          `[JVFeaturedProperties:fetchJVRow] > [Error]: ${error?.message || "unknown"}`
        );
        setJvPropertyIds([]);
      } finally {
        setLoadingRow(false);
      }
    };

    fetchJVRow();
  }, [data]);

  if (isError) return null;

  const hasJVProperties = jvPropertyIds.length > 0;

  return (
    <section className="bg-[#F5F3EF] py-14 sm:py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 text-center sm:mb-10">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#C1694F] sm:text-sm">
            Available Now
          </p>
          <h2 className="mt-2 text-2xl font-bold text-[#16213E] sm:text-4xl">
            Featured Joint Venture Properties
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-slate-600 sm:text-base">
            Hand-picked partnership opportunities currently in our pipeline.
          </p>
        </div>

        {isLoading || loadingRow ? (
          <div className="flex justify-center py-16">
            <PuffLoader size={60} color="#C1694F" />
          </div>
        ) : hasJVProperties ? (
          <DisplayGrid
            properties={data}
            filter={createGridFilter.featured("all", jvPropertyIds)}
          />
        ) : (
          <p className="py-10 text-center text-slate-600">
            New joint venture opportunities are added regularly — check back soon.
          </p>
        )}

        <div className="mt-8 flex justify-center sm:mt-10">
          <a
            href="/properties"
            className="inline-block rounded-full bg-[#16213E] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#0d1626] sm:text-base"
            id="jv-featured-link-browse-all"
          >
            View All Properties
          </a>
        </div>
      </div>
    </section>
  );
};

export default JVFeaturedProperties;
